import { apiGetSharedLinkItemDownloadableLink, apiGetSharedListFromLink } from '@/api/shareLinks'
import router from '@/router'
import { simulateMultiLinkClick, getPathFromLocation } from '@/services/files'
import { routerPush } from '@/services/routing'
import { initRequestHelper, isAlreadyPending } from '@/store/root'
import { ActionTree, GetterTree, Module, MutationTree } from 'vuex'
import { Directory, File, FileSystemItem } from '@/store/types/file'
import { SharedLinksState, SharedLinkBrowse, SharedItem } from '@/store/types/sharedFile'
import { RootState } from '@/store/types/root'
import { getHash } from '@/services/sharedLinks'

const getDefaultState = (): SharedLinksState => {
  return {
    hash: '',
    directories: [],
    files: [],
    selectedPaths: new Set(),
    path: '/',
    permissions: {
      upload: false,
      download: false,
      list: false,
    },
    linkTitle: '',
  }
}

const moduleState = getDefaultState()

const moduleGetters: GetterTree<SharedLinksState, RootState> = {
  files(state) {
    return state.files
  },
  directories(state) {
    return state.directories
  },
  loadFilesRequest(_state, _getters, rootState) {
    return rootState.requests.loadFiles
  },
  loadFilesRequestPending(_state, getters) {
    return isAlreadyPending(getters.loadFilesRequest)
  },
  shareLinkRequest(_state, _getters, rootState) {
    return rootState.requests.linkShare
  },
  directoryNavigateRequest(_state, _getters, rootState) {
    return rootState.requests.directoryNavigate
  },
  path(state) {
    return state.path
  },
  hash(state) {
    return state.hash
  },
  selectedPaths(state) {
    return state.selectedPaths
  },
  selectedDirectories(state, getters): Directory[] {
    return getters.directories.filter((d: Directory) => state.selectedPaths.has(d.name))
  },
  selectedFiles(state, getters): File[] {
    return getters.files.filter((f: File) => state.selectedPaths.has(f.name))
  },
  selectedItems(_state, getters): FileSystemItem[] {
    return getters.selectedDirectories.concat(getters.selectedFiles)
  },
  singleSelectedItem(_state, getters): FileSystemItem | undefined {
    if (getters.selectedItems.length == 1) {
      return getters.selectedItems[0]
    }
    return undefined
  },
  numberOfSelected(state) {
    return state.selectedPaths.size
  },
  permissions(state) {
    return state.permissions
  },
  linkTitle(state) {
    return state.linkTitle
  },
}

const moduleActions: ActionTree<SharedLinksState, RootState> = {
  async goToDirectory({ state, dispatch, commit }, path = '/') {
    const [setSuccess, setFailure] = initRequestHelper(commit, 'directoryNavigate')
    try {
      await routerPush(router, `/shared-link/${state.hash}/?directory=${path}`)
      await dispatch('loadSharedDirectory')
      setSuccess()
    } catch (err: any) {
      setFailure(err.message)
      throw err
    }
  },

  async loadSharedDirectory({ commit, dispatch, getters }) {
    const pathMatch = router.currentRoute.params.pathMatch
    commit('clearSelection')
    const isDirectory = router.currentRoute.query.directory
    const path = !!isDirectory
      ? String(router.currentRoute.query.directory)
      : getPathFromLocation(pathMatch)
    commit('setPath', path)

    const [setSuccess, setFailure] = initRequestHelper(commit, 'loadFiles')
    try {
      const hash = getHash(pathMatch)
      const requestPath = !!isDirectory ? `?directory=${isDirectory}/` : path
      commit('setHash', hash)
      const data: SharedLinkBrowse = await apiGetSharedListFromLink(hash, requestPath)
      const files = data.items?.filter((i: SharedItem) => i.type === 'file')
      const directories = data.items?.filter((i: SharedItem) => i.type === 'directory')
      commit('setData', {
        files,
        directories,
      })
      commit('setPermissions', data.permissions)
      commit('setLinkTitle', data.title)
      setSuccess()
    } catch (err: any) {
      setFailure(err.message)
      throw err
    }
  },

  async downloadMultiple({ commit, getters }) {
    const [setSuccess, setFailure] = initRequestHelper(commit, 'loadFiles')
    const arrayOfSelectedPaths: string[] = Array.from(getters.selectedPaths) // so we can freely use .map()

    try {
      const downloadableLinks = await Promise.all(
        arrayOfSelectedPaths.map((selectedPath: string) => {
          const isRoot = getters.path == '/'
          const fullPath = isRoot ? selectedPath : getters.path + '/' + selectedPath
          return apiGetSharedLinkItemDownloadableLink(getters.hash, fullPath)
        }),
      )

      simulateMultiLinkClick(downloadableLinks)
      setSuccess()
    } catch (e: any) {
      setFailure(e.message)
      throw e
    }
  },

  async clearSelection({ commit }) {
    commit('clearSelection')
  },
  async clearBrowser({ commit }) {
    commit('clearDriveSelection')
    commit('clearPath')
    commit('clearData')
  },
}

const moduleMutations: MutationTree<SharedLinksState> = {
  setHash(state, hash) {
    state.hash = hash
  },
  clearSelection(state) {
    state.selectedPaths = new Set()
  },
  selectPath(state, path) {
    if (state.selectedPaths.has(path)) return
    const copy = new Set(state.selectedPaths)
    copy.add(path)
    state.selectedPaths = copy
  },
  deselectPath(state, path) {
    if (!state.selectedPaths.has(path)) return
    const copy = new Set(state.selectedPaths)
    copy.delete(path)
    state.selectedPaths = copy
  },
  selectMultiplePaths(state, paths: string[]): void {
    const copy = new Set(state.selectedPaths)
    paths.map((path: string) => copy.add(path))
    state.selectedPaths = copy
  },
  setPath(state, path) {
    state.path = path
  },
  clearPath(state) {
    state.path = '/'
  },
  setData(state, { files, directories }) {
    state.files = files
    state.directories = directories
  },
  setPermissions(state, permissions) {
    state.permissions = permissions
  },
  setLinkTitle(state, linkTitle) {
    state.linkTitle = linkTitle
  },
  clearData(state) {
    state.files = []
    state.directories = []
  },
  resetState(state) {
    Object.assign(state, getDefaultState())
  },
}

const sharedFile: Module<SharedLinksState, RootState> = {
  namespaced: true,
  // prefixed with "module-", so that they do not get shadowed by desctructured context
  state: moduleState,
  getters: moduleGetters,
  actions: moduleActions,
  mutations: moduleMutations,
}
export default sharedFile
